// ============================================================================
// MATRIZ MODULAR DE PROCURAÇÕES 2026 (BASEADA NO DEEP RESEARCH T.C.R.E.I.)
// Sincronizado com o mestre do Firestore (procuration_templates/master_v1) em 07/08/2026.
// Ver CONSTITUICAO_REGRAS.SH Art. 337 para o runbook de sincronização local<->nuvem.
//
// GÊNERO DO TERMO INSTITUCIONAL ("a parte OUTORGANTE"/"OUTORGADA") — FIXO,
// NÃO VARIÁVEL. Ver CONSTITUICAO_REGRAS.SH Art. 381 para a análise completa.
// Resumo: `formData.parties` é ARRAY (múltiplos outorgantes reais, já
// suportados na UI) — um sistema de artigo variável (o/a) exigiria resolver
// singular-M/singular-F/plural-M/plural-misto, e o caso de grupo misto cai
// no mesmo problema de linguagem inclusiva que a convenção fixa (singular e
// feminino, independente de gênero/pluralidade) existe para evitar. Isto é
// restrição técnica real, não escolha de estilo — não confundir com a
// Declaração, que é variável porque nunca tem mais de 1 declarante.
// ============================================================================

export const STATIC_CONTENT = {
  header: `
        <div class="text-center border-b-4 border-[#20243B] pb-6 mb-8">
            <h1 class="text-3xl font-black text-[#20243B] uppercase mb-2 tracking-tight" style="font-family: 'Merriweather', serif;">Procuração <em>Ad Judicia et Extra</em></h1>
            <p class="text-sm font-bold text-gray-600 tracking-widest uppercase">Rodrigo Demeterco Advocacia | OAB/PR n. 113.938</p>
        </div>
    `,
  footer: `<div class="text-center text-xs text-gray-400 mt-12 pt-8 border-t border-gray-200">Documento assinado digitalmente nos termos da Lei n. 14.063/2020 (Lei das Assinaturas Eletrônicas) e REsp n. 2.243.445/SP.<br>A autenticidade pode ser verificada no portal <strong>assinador.iti.br</strong>.</div>`,
};

// --- BIBLIOTECA DE CLÁUSULAS (LEGO JURÍDICO) ---
// Fonte: mestre do Firestore. NÃO editar clausula-a-clausula aqui sem
// depois espelhar a mudança no Firestore (ou vice-versa) — ver Art. 337.
export const POWERS_LIBRARY = [
  {
    items: null,
    listStyle: "NUM",
    content: "            <p>Considerando que a postulação perante o Poder Judiciário e a defesa técnica de interesses exigem atuação jurídica especializada e estratégica;</p><p>Considerando que a advocacia é função essencial e indispensável à administração da justiça, nos estritos termos do <strong>art. 133 da Constituição Federal de 1988</strong>;</p><p>As partes, devidamente qualificadas na [[REF_CLAUSE:cl_01_partes]], firmam o presente instrumento de mandato com o propósito de consolidar a representação jurídica, visando garantir a transparência radical, a clareza na outorga de poderes e a excelência profissional na prestação dos serviços.</p><p>Para fins de padronização, fluidez de leitura e segurança processual, este instrumento adotará a variável gramatical \"a parte <strong>OUTORGANTE</strong>\" para referir-se ao polo tomador dos serviços e concessor dos poderes, independentemente do seu gênero (masculino ou feminino), da sua natureza (física, jurídica, animal, menor ou espólio) ou de sua pluralidade (litisconsórcio). De igual modo, quando a referência for ao polo prestador do serviço, utilizar-se-á a expressão institucional \"a parte <strong>OUTORGADA</strong>\", designação genérica e invariável que abrange a sociedade de advogados, seu titular e eventuais advogados associados ou parceiros qualificados na [[REF_CLAUSE:cl_01_partes]] deste instrumento. Sendo assim, toda a concordância verbal e nominal das cláusulas a seguir obedecerá estritamente a esta regra (singular e feminino).</p><p>Ressalva-se que a padronização de gênero gramatical aqui adotada tem finalidade exclusivamente técnica de uniformização redacional, não refletindo qualquer declaração sobre o gênero ou natureza das partes, assegurando-se a validade do instrumento independentemente de eventuais usos gramaticais divergentes da concordância tradicional.</p>        ",
    id: "preambulo",
    tags: [
      "Universal"
    ],
    title: "PREÂMBULO E DIRETRIZES DE INTERPRETAÇÃO",
    type: "text"
  },
  {
    id: "cl_01_partes",
    children: [
      {
        content: "            <p><strong>PARTE OUTORGANTE:</strong> [[QUALIFICACAO_CONTRATANTE]]</p>        ",
        id: "sub_1773702586852",
        condition: "ALWAYS",
        type: "sub_clause"
      },
      {
        type: "sub_clause",
        condition: "ALWAYS",
        id: "sub_1773702604874",
        content: "<strong>PARTE OUTORGADA:</strong> [[QUALIFICACAO_CONTRATADO]]"
      }
    ],
    title: "DAS PARTES",
    tags: [
      "Universal"
    ],
    items: null,
    content: "",
    condition: "ALWAYS",
    type: "sub_clause_group"
  },
  {
    title: "DO OBJETO DO MANDATO",
    id: "cl_02_objeto",
    type: "sub_clause_group",
    children: [
      {
        condition: "ALWAYS",
        id: "cl_02_01_objeto_base",
        type: "sub_clause",
        content: "Por meio do presente instrumento, a parte <strong>OUTORGANTE</strong> nomeia a parte <strong>OUTORGADA</strong> como sua bastante procuradora, conferindo-lhe poderes específicos para a representação técnica e defesa de seus interesses atrelados ao seguinte escopo: <strong>[[OBJETO_PRINCIPAL]]</strong>.",
        level: 0
      },
      {
        content: "<strong>Cláusula de Especialidade Patrimonial:</strong> Caso o objeto deste mandato, ou eventuais acordos dele decorrentes, abranja alienação, oneração, renúncia, promessa de venda, dação em pagamento de bens imóveis ou disposição de direitos reais, tais bens e/ou direitos ficam desde já estritamente individualizados da seguinte forma: [[BENS_IMOVEIS_ESPECIFICOS]], em observância ao princípio da especialidade e ao art. 661, § 1º, da Lei n. 10.406/2002 (Código Civil), que exige poderes especiais e expressos para os atos de disposição patrimonial imobiliária.",
        id: "cl_02_02_especialidade_patrimonial",
        condition: "PATRIMONIAL_IMOBILIARIO_ONLY",
        level: 0,
        type: "sub_clause"
      },
      {
        id: "cl_02_03_contexto_adverso",
        condition: "HAS_PARTE_CONTRARIA",
        level: 0,
        content: "[[CONTEXTO_PROCESSUAL]]",
        type: "sub_clause"
      }
    ],
    content: "",
    condition: "ALWAYS",
    tags: [
      "Universal"
    ]
  },
  {
    children: [
      {
        level: 0,
        type: "sub_clause",
        content: "<p>Em estrita observância ao art. 105 da Lei n. 13.105/2015 (Código de Processo Civil), conferem-se à parte <strong>OUTORGADA</strong> os poderes da cláusula <em>ad judicia</em> e, adicional e especificamente, os poderes especiais enumerados nesta cláusula, habilitando-a a atuar em qualquer juízo, instância ou tribunal (Estadual, Federal, Trabalhista, Eleitoral, Militar e Cortes Superiores), podendo propor as ações competentes, apresentar defesas e recursos, acompanhando os feitos até o fiel cumprimento de sentença ou exaurimento da via administrativa, e especialmente:</p>",
        condition: "ALWAYS",
        id: "cl_04_01_intro"
      },
      {
        level: 1,
        content: "receber citação inicial e intimações pessoais (inclusive por meios eletrônicos e aplicativos de mensagens interativas, com lastro na instrumentalidade das formas) e confessar fatos;",
        type: "sub_clause",
        id: "cl_04_01_01_citacao_confissao",
        condition: "ALWAYS"
      },
      {
        level: 1,
        content: "reconhecer a procedência do pedido;",
        id: "cl_04_01_02_reconhecer_pedido",
        condition: "ALWAYS",
        type: "sub_clause"
      },
      {
        content: "transigir, conciliar e firmar acordos judiciais e extrajudiciais;",
        condition: "ALWAYS",
        id: "cl_04_01_03_transigir",
        type: "sub_clause",
        level: 1
      },
      {
        type: "sub_clause",
        content: "desistir da demanda e renunciar ao direito sobre o qual se funda a ação;",
        condition: "ALWAYS",
        id: "cl_04_01_04_desistir_renunciar",
        level: 1
      },
      {
        level: 1,
        type: "sub_clause",
        id: "cl_04_01_05_renunciar_prazos",
        condition: "ALWAYS",
        content: "renunciar a prazos processuais e recursais;"
      },
      {
        condition: "ALWAYS",
        type: "sub_clause",
        level: 1,
        id: "cl_04_01_06_receber_quitacao",
        content: "receber valores e dar quitação plena;"
      },
      {
        type: "sub_clause",
        level: 1,
        content: "firmar compromisso arbitral;",
        condition: "ALWAYS",
        id: "cl_04_01_07_compromisso_arbitral"
      },
      {
        id: "cl_04_01_08_hipossuficiencia",
        condition: "ALWAYS",
        type: "sub_clause",
        level: 1,
        content: "<p>assinar declaração de hipossuficiência econômica (pedido de Justiça Gratuita), assumindo a parte <strong>OUTORGANTE</strong> integral e exclusiva responsabilidade pelas informações prestadas, sujeitando-se, em caso de inveracidade, às sanções por litigância de má-fé dos arts. 79 a 81 da Lei n. 13.105/2015 (Código de Processo Civil).</p>"
      }
    ],
    type: "sub_clause_group",
    condition: "ALWAYS",
    title: "DOS PODERES JUDICIAIS, EXTRAJUDICIAIS E ESPECIAIS",
    content: "",
    tags: [
      "Universal"
    ],
    id: "cl_04_especiais_comuns"
  },
  {
    children: [
      {
        condition: "RAMO_CIVIL_CONSUMIDOR",
        content: "<p><strong>Advocacia Cível, Consumerista e Juizados:</strong> Conferem-se poderes específicos para representar a parte <strong>OUTORGANTE</strong> perante órgãos de proteção ao consumidor (PROCON) e plataformas governamentais (Consumidor.gov.br). Outorgam-se poderes expressos para atuação perante os Juizados Especiais Cíveis (JEC), autorizando a parte <strong>OUTORGADA</strong> a requerer homologações, levantar alvarás, firmar termo de fiel depositário e, observando o melhor interesse da parte <strong>OUTORGANTE</strong>, renunciar expressamente ao crédito excedente ao teto legal aplicável (art. 3º, § 3º, da Lei n. 9.099/1995 — Lei dos Juizados Especiais).</p>",
        level: 0,
        id: "cl_05_01_setorial_dinamico",
        type: "sub_clause"
      },
      {
        condition: "RAMO_CIVIL_FIEL_DEPOSITARIO",
        content: "<p><strong>Fiel [[DEPOSITARIO_GENERO]] Judicial (Cível):</strong> Investida a parte <strong>OUTORGANTE</strong> na condição de fiel depositária de bem penhorado, arrestado, sequestrado ou objeto de outra constrição judicial, nos termos dos arts. 665, inciso IV, e 840, inciso II, § 1º, da Lei n. 13.105/2015 (Código de Processo Civil), conferem-se à parte <strong>OUTORGADA</strong> poderes específicos para firmar termo de compromisso de fiel [[depositario_genero]], requerer sua substituição, redução ou levantamento da constrição, e exibir o bem em juízo quando determinado, ressalvado que a assunção do encargo e a responsabilidade civil e penal por eventual infidelidade constituem ato personalíssimo da parte <strong>OUTORGANTE</strong>, insuscetível de delegação.</p>",
        level: 0,
        id: "cl_05_01b_fiel_depositario_civil",
        type: "sub_clause"
      },
      {
        condition: "RAMO_MANDADO_SEGURANCA_IMPETRANTE",
        level: 0,
        type: "sub_clause",
        id: "cl_05_00_mandado_seguranca_impetrante",
        content: "<p><strong>Mandado de Segurança — Polo Ativo (Impetrante):</strong> Em estrita observância à Lei n. 12.016/2009, conferem-se à parte <strong>OUTORGADA</strong> poderes específicos e expressos para impetrar Mandado de Segurança — individual ou coletivo, de natureza cível, criminal, tributária ou administrativa — em favor da parte <strong>OUTORGANTE</strong>, contra o ato ilegal ou abusivo praticado por [[AUTORIDADE_COATORA_NOME]], na qualidade de autoridade coatora vinculada a [[AUTORIDADE_COATORA_ORGAO]], consistente em: [[ATO_COATOR_DESCRICAO]], cuja ciência inequívoca se deu em [[ATO_COATOR_DATA]], podendo, para tanto, requerer a concessão de medida liminar, inclusive <em>inaudita altera parte</em>, prestar esclarecimentos e informações complementares ao juízo, interpor os recursos cabíveis em todas as instâncias, inclusive perante as Cortes Superiores, e praticar todos os demais atos necessários ao regular processamento do <em>mandamus</em> até a concessão definitiva da segurança.</p>",
      },
      {
        condition: "RAMO_MANDADO_SEGURANCA_IMPETRADO",
        level: 0,
        type: "sub_clause",
        id: "cl_05_00_mandado_seguranca_impetrado",
        content: "<p><strong>Mandado de Segurança — Polo Passivo (Impetrado):</strong> Conferem-se à parte <strong>OUTORGADA</strong> poderes específicos e expressos para representar a parte <strong>OUTORGANTE</strong> na defesa de ato próprio ou de interesse jurídico por ela detido e impugnado por Mandado de Segurança, seja na qualidade de autoridade coatora, seja na de litisconsorte passivo necessário ou terceiro juridicamente interessado no ato atacado, relativamente a: [[ATO_COATOR_DESCRICAO]], podendo, para tanto, prestar as informações previstas no art. 7º, inciso I, da Lei n. 12.016/2009, contestar o mérito da impetração, impugnar o pedido de liminar e requerer sua cassação, revogação ou suspensão de segurança, interpor Agravo Interno, Recurso Ordinário em Mandado de Segurança (RMS) e demais recursos cabíveis em todas as instâncias, inclusive perante as Cortes Superiores, e praticar todos os demais atos necessários à defesa do ato impugnado.</p>",
      },
      {
        condition: "RAMO_TRABALHISTA",
        level: 0,
        type: "sub_clause",
        id: "cl_05_02_trabalhista",
        content: "<p><strong>Advocacia Trabalhista:</strong> Em observância ao item I da Súmula 395 do TST e ao art. 105, § 4º, da Lei n. 13.105/2015 (Código de Processo Civil), este instrumento contém cláusula expressa, ora pactuada, de que a vigência deste mandato na seara laboral estende-se ininterruptamente até o trânsito em julgado e arquivamento definitivo da lide. Conferem-se poderes para representar a parte <strong>OUTORGANTE</strong> perante a Justiça do Trabalho em todas as instâncias, Ministério Público do Trabalho, Superintendências Regionais do Trabalho e Emprego, Sistema de Procuração Eletrônica (SPE), FGTS Digital e Domicílio Eletrônico Trabalhista (DET), demais órgãos administrativos trabalhistas, podendo firmar acordos em audiências de conciliação, participar de mediações e negociações coletivas, requerer a expedição de alvarás e levantar depósitos recursais.</p>"
      },
      {
        id: "cl_05_02b_previdenciario",
        content: "<p><strong>Advocacia Previdenciária:</strong> Perante o Instituto Nacional do Seguro Social (INSS), nos estritos termos da Instrução Normativa PRES/INSS n. 128/2022, conferem-se poderes específicos para requerer, acompanhar e recorrer de benefícios previdenciários e assistenciais, cadastrar e recuperar senhas no portal Meu INSS, realizar comprovação de vida perante a rede bancária, receber mensalidades e verbas pretéritas em atraso, bem como representar a parte <strong>OUTORGANTE</strong> perante Juntas de Recursos e Câmaras de Julgamento do Conselho de Recursos da Previdência Social (CRPS).</p>",
        condition: "RAMO_PREVIDENCIARIO",
        type: "sub_clause",
        level: 0
      },
      {
        id: "sub_1773707462162",
        type: "sub_clause",
        condition: "RAMO_PENAL_PUBLICA_PASSIVO",
        content: "<p><strong>Advocacia Penal — Ação Pública (Polo Passivo: Investigado e Réu):</strong> Conferem-se poderes amplos para o patrocínio da defesa criminal em face da persecução estatal, compreendendo as seguintes prerrogativas:</p>"
      },
      {
        level: 1,
        condition: "ALWAYS",
        type: "sub_clause",
        content: "<p><strong>Acesso Inquisitorial e Sigilo:</strong> Autoriza-se o acesso irrestrito a inquéritos e procedimentos investigatórios, inclusive os gravados sob sigilo absoluto, com amparo na Súmula Vinculante 14 do STF.</p>",
        id: "sub_1773707480400"
      },
      {
        level: 1,
        condition: "ALWAYS",
        type: "sub_clause",
        content: "<p><strong>Justiça Negocial e Acordos:</strong> Na esfera da Justiça Negocial, outorgam-se poderes para transigir e negociar Acordos de Não Persecução Penal (ANPP), Transação Penal e Colaboração Premiada (Lei n. 12.850/2013 — Lei das Organizações Criminosas), assistindo a parte <strong>OUTORGANTE</strong> na celebração do ANPP, cuja confissão formal constitui ato pessoal do investigado, a ser ratificada em audiência.</p>",
        id: "sub_1773707498870"
      },
      {
        id: "sub_1773707512188",
        level: 1,
        condition: "ALWAYS",
        type: "sub_clause",
        content: "<p><strong>Tribunal do Júri:</strong> No rito do Tribunal do Júri, garante-se à parte <strong>OUTORGADA</strong> a independência tática para, em plenário, dispensar ou renunciar à oitiva de testemunhas sem prévia anuência.</p>"
      },
      {
        id: "sub_1773707525307",
        type: "sub_clause",
        level: 1,
        condition: "ALWAYS",
        content: "<p><strong>Execução Penal (VEP):</strong> Na fase de Execução Penal (VEP), conferem-se poderes expressos para assinar termos de responsabilidade de monitoração eletrônica (tornozeleira), progressão de regime e livramento condicional em nome da parte <strong>OUTORGANTE</strong>.</p>"
      },
      {
        id: "sub_1773709321510",
        condition: "RAMO_PENAL_PUBLICA_ATIVO",
        content: "<p><strong>Advocacia Penal — Ação Pública (Polo Ativo: Vítima e Assistente):</strong> Confere-se legitimação extraordinária para a atuação incisiva na tutela dos interesses da vítima na persecução penal de iniciativa pública, compreendendo as seguintes outorgas:</p>",
        type: "sub_clause"
      },
      {
        condition: "ALWAYS",
        content: "<p><strong>Atuação Inquisitorial Ativa, Medidas Protetivas e Oposição a Arquivamento:</strong> Autoriza-se a representação irrestrita perante autoridades policiais (Delegacias) e Ministério Público na fase investigatória. Em estrita observância ao art. 14 do Decreto-Lei n. 3.689/1941 (Código de Processo Penal), outorgam-se poderes expressos para assumir postura ativa na construção do acervo probatório, podendo o advogado requerer diligências investigatórias, propor a oitiva de testemunhas e informantes, formular quesitos para perícias e juntar documentos e laudos técnicos diretamente à autoridade policial. Confere-se, ainda, poder expresso para requisitar acesso a inquéritos sigilosos (Súmula Vinculante 14 do STF), postular a fixação de Medidas Protetivas de Urgência, requerer a decretação de Medidas Cautelares Pessoais contra o agressor e apresentar oposição fundamentada a eventuais arquivamentos do inquérito (art. 28 do Decreto-Lei n. 3.689/1941 — Código de Processo Penal).</p>",
        type: "sub_clause",
        level: 1,
        id: "sub_1773709488317"
      },
      {
        condition: "ALWAYS",
        id: "sub_1773709507339",
        level: 1,
        content: "<p><strong>Assistente de Acusação, Reparação e Júri:</strong> Instaurada a fase judicial, autoriza-se a habilitação como Assistente de Acusação (art. 268 do Decreto-Lei n. 3.689/1941 — Código de Processo Penal), com poderes irrestritos para arrolar testemunhas, postular diligências assecuratórias patrimoniais, inquirir réus, atuar ativamente nos debates em Plenário do Tribunal do Júri e pleitear a fixação de valor pecuniário mínimo para a reparação integral dos danos civis, materiais e morais (art. 387, inciso IV, do mesmo Código).</p>",
        type: "sub_clause"
      },
      {
        level: 1,
        id: "sub_1773709521466",
        content: "<p><strong>Vigilância Negocial e Recursos:</strong> Garantem-se poderes para impetrar Mandado de Segurança Criminal em favor da vítima, interpor recursos autônomos ou supletivos nas instâncias ordinárias e superiores (art. 271 do Decreto-Lei n. 3.689/1941 — Código de Processo Penal), bem como para intervir em audiências de ANPP exigindo a justa e imediata reparação financeira à vítima como condição para homologação do acordo (art. 28-A, inciso I e § 14, do mesmo Código).</p>",
        type: "sub_clause",
        condition: "ALWAYS"
      },
      {
        content: "<p><strong>Advocacia Penal — Ação Privada (Polo Passivo: Querelado):</strong> Conferem-se poderes amplos e estritos para o patrocínio defensivo em face de acusação penal de iniciativa privada, compreendendo as seguintes prerrogativas processuais:</p>",
        condition: "RAMO_PENAL_PRIVADA_PASSIVO",
        type: "sub_clause",
        id: "sub_1773753677914"
      },
      {
        condition: "ALWAYS",
        content: "<p><strong>Audiência de Reconciliação e Retratação:</strong> Em estrita observância ao art. 520 do Decreto-Lei n. 3.689/1941 (Código de Processo Penal), outorgam-se poderes especiais para representar a parte <strong>OUTORGANTE</strong> em audiências preliminares de conciliação, possuindo autoridade para transigir, firmar termos de retratação formal ou composição civil de danos, visando a rejeição liminar da Queixa-Crime.</p>",
        id: "sub_1773753693328",
        level: 1,
        type: "sub_clause"
      },
      {
        id: "sub_1773753704863",
        content: "<p><strong>Aceitação de Perdão e Disposição do Mérito:</strong> Conferem-se poderes expressos, personalíssimos e irrevogáveis para aceitar o perdão do ofendido (art. 106 do Decreto-Lei n. 2.848/1940 — Código Penal), resultando na extinção da punibilidade, bem como poderes para formular e apresentar Exceção da Verdade ou Notoriedade em crimes contra a honra, gerindo integralmente a estratégia de defesa em Juizados Especiais Criminais (JECRIM) ou varas comuns.</p>",
        condition: "ALWAYS",
        level: 1,
        type: "sub_clause"
      },
      {
        level: 0,
        id: "sub_1773753716086",
        content: "<p><strong>Advocacia Penal — Ação Privada (Polo Ativo: Querelante):</strong> Confere-se legitimação extraordinária para a atuação no polo ativo da persecução penal de iniciativa privada, compreendendo as seguintes outorgas e responsabilidades:</p>",
        type: "sub_clause",
        condition: "RAMO_PENAL_PRIVADA_ATIVO"
      },
      {
        id: "sub_1773753757913",
        level: 1,
        type: "sub_clause",
        content: "<p><strong>Queixa-Crime:</strong> Em obediência estrita ao art. 44 do Decreto-Lei n. 3.689/1941 (Código de Processo Penal), outorgam-se poderes especiais, específicos e expressos para a promoção e ajuizamento de QUEIXA-CRIME em face de [[NOME_DO_QUERELADO_REU]], englobando a totalidade da descrição fática e delituosa constante na respectiva peça vestibular subscrita pelo patrono.</p>",
        condition: "ALWAYS"
      },
      {
        level: 1,
        type: "sub_clause",
        content: "<p><strong>Disponibilidade da Ação e Perdão:</strong> Conferem-se, de modo correlato e exclusivo à ação penal privada, poderes de disposição processual aptos a negociar composição civil, renunciar ao direito de queixa (art. 50 do Decreto-Lei n. 3.689/1941 — Código de Processo Penal), declarar perempção ou, no melhor interesse estratégico, oferecer formalmente o perdão ao ofensor (art. 106 do Decreto-Lei n. 2.848/1940 — Código Penal).</p>",
        condition: "ALWAYS",
        id: "sub_1773753798301"
      },
      {
        condition: "RAMO_PENAL_FIEL_DEPOSITARIO",
        content: "<p><strong>Fiel [[DEPOSITARIO_GENERO]] Judicial (Restituição de Coisas Apreendidas):</strong> Nomeada a parte <strong>OUTORGANTE</strong> fiel depositária de bem apreendido nos autos, nos termos dos arts. 118 a 124-A do Decreto-Lei n. 3.689/1941 (Código de Processo Penal), conferem-se à parte <strong>OUTORGADA</strong> poderes específicos para requerer a restituição do bem, acompanhar e impugnar decisões relativas à guarda e conservação da coisa depositada, e postular a substituição ou liberação do encargo, ressalvado que a assunção do encargo e a responsabilidade civil e penal por eventual infidelidade constituem ato personalíssimo da parte <strong>OUTORGANTE</strong>.</p>",
        level: 0,
        id: "cl_05_02c_fiel_depositario_penal",
        type: "sub_clause"
      },
      {
        content: "<p><strong>Advocacia Societária e Empresarial:</strong> Em conformidade com as diretrizes do DREI (Instruções Normativas n. 81/2020 e n. 01/2025), outorgam-se poderes para representar a pessoa jurídica perante Juntas Comerciais e portais integradores da REDESIM, habilitando a parte <strong>OUTORGADA</strong> a firmar alterações contratuais, defender o nome empresarial e atestar a autenticidade documental eletrônica utilizando seu próprio certificado digital.</p>",
        type: "sub_clause",
        condition: "RAMO_SOCIETARIO_EMPRESARIAL",
        id: "sub_1773709556679"
      },
      {
        type: "sub_clause",
        condition: "RAMO_ADMINISTRATIVO",
        content: "<p><strong>Advocacia Administrativa:</strong> Conferem-se poderes específicos para representar a parte <strong>OUTORGANTE</strong> perante entes da Administração Direta e Indireta (federal, estadual e municipal), autarquias, fundações públicas, empresas públicas, sociedades de economia mista e conselhos de classe. A parte <strong>OUTORGADA</strong> fica expressamente autorizada a atuar em processos licitatórios, sindicâncias, processos administrativos disciplinares (PAD), defesas contra autos de infração e multas administrativas, impetrar Mandados de Segurança e Habeas Data contra atos de autoridade pública, bem como firmar termos de ajustamento de conduta ou compromisso administrativo perante qualquer órgão ou entidade integrante da Administração Pública.</p>",
        level: 0,
        id: "cl_05_08_administrativo"
      },
      {
        id: "cl_05_08b_tributario",
        content: "<p><strong>Advocacia Tributária (e-CAC da Receita Federal):</strong> Conferem-se poderes para representar a parte <strong>OUTORGANTE</strong> perante a Secretaria da Receita Federal do Brasil, PGFN, CARF, Secretarias de Fazenda estaduais e municipais e demais órgãos tributários. A parte <strong>OUTORGADA</strong> fica expressamente autorizada a atuar no Centro Virtual de Atendimento (e-CAC), convalidar, aceitar, validar ou rejeitar autorizações de acesso delegadas eletronicamente, gerir dossiês digitais de atendimento, transigir em programas de regularização tributária e praticar atos de defesa administrativa, inclusive em sindicâncias e autos de infração fiscal.</p>",
        condition: "RAMO_TRIBUTARIO",
        type: "sub_clause",
        level: 0
      },
      {
        id: "cl_familia_animal_add_familia",
        type: "sub_clause",
        level: 0,
        condition: "RAMO_FAMILIA",
        content: "<strong>Advocacia de Família e Sucessões:</strong> Conferem-se poderes especiais para a parte <strong>OUTORGADA</strong> representar a parte <strong>OUTORGANTE</strong> perante Cartórios de Notas e Registro Civil, autorizando a assinatura e lavratura de escrituras públicas de divórcio, dissolução de união estável e pactos antenupciais, nos termos do art. 733 da Lei n. 13.105/2015 (Código de Processo Civil), regulamentado pela Lei n. 11.441/2007. No âmbito sucessório, outorga-se capacidade para atuar em inventários extrajudiciais, nos termos do art. 610, § 1º, do mesmo Código, podendo a parte <strong>OUTORGADA</strong> ser nomeada inventariante, ceder direitos hereditários, assinar plano de partilha e prestar primeiras e últimas declarações, ficando ressalvada a necessidade de instrumento autônomo específico para eventual alienação ou oneração de bens imóveis."
      },
      {
        id: "cl_familia_animal_add_animal",
        type: "sub_clause",
        level: 0,
        condition: "RAMO_ANIMAL",
        content: "<strong>Advocacia de Direito Animal (Representação Multiespécie):</strong> Reconhecendo a condição do animal não humano como sujeito de direitos despersonificado, nos termos da jurisprudência do Tribunal de Justiça do Paraná e da doutrina contemporânea sobre capacidade processual multiespécie, a parte <strong>OUTORGANTE</strong> atua neste instrumento não apenas em nome próprio, mas também como representante processual, guardiã fiduciária e substituta legal do animal não humano sob sua tutela, individualizado conforme qualificação constante deste instrumento ou de termo específico anexo. Conferem-se à parte <strong>OUTORGADA</strong> poderes para postular em nome do animal representado, formar litisconsórcio ativo entre a parte <strong>OUTORGANTE</strong> e o animal representado, requerer tutelas de urgência, medidas protetivas, indenização por danos materiais e morais em favor do animal, e praticar todos os demais atos processuais e extrajudiciais necessários à tutela de seus interesses."
      }
    ],
    id: "cl_05_setorial",
    type: "sub_clause_group",
    title: "DOS PODERES ESPECÍFICOS",
    condition: "ALWAYS",
    tags: [
      "Universal"
    ],
    content: ""
  },
  {
    tags: [
      "Universal"
    ],
    content: "",
    type: "sub_clause_group",
    id: "cl_06_financeiros",
    condition: "ALWAYS",
    children: [
      {
        type: "sub_clause",
        condition: "ALWAYS",
        content: "<p>Fica a parte <strong>OUTORGADA</strong> expressamente autorizada a atuar perante instituições financeiras (<strong>Banco do Brasil</strong>, <strong>Caixa Econômica Federal</strong>) e plataformas de depósito judicial (ex: <strong>SISCONDJ</strong>), possuindo poderes literais e específicos para requerer, levantar, sacar e receber alvarás judiciais, precatórios e RPVs, bem como para <strong>dar e receber quitação plena e irrevogável</strong>. Resta expressamente consignada aos gerentes e prepostos bancários a inexigibilidade e dispensa, por manifesta ilegalidade, da apresentação de \"Certidão de Militância\" contemporânea para a efetivação dos referidos saques.</p>",
        id: "cl_06_01_alvaras",
        level: 0
      },
      {
        id: "cl_06_02_destaque_honorarios",
        type: "sub_clause",
        condition: "ALWAYS",
        content: "<p><strong>Destaque de Honorários Contratuais:</strong> Em conformidade com o art. 22, § 4º, da Lei n. 8.906/1994 (Estatuto da Advocacia e da OAB), a parte <strong>OUTORGANTE</strong> autoriza expressamente a parte <strong>OUTORGADA</strong> a juntar aos autos o respectivo contrato de honorários antes da expedição do mandado de levantamento, alvará ou precatório, requerendo ao juízo que determine o pagamento direto à parte <strong>OUTORGADA</strong> do valor correspondente aos honorários contratuais ali pactuados, mediante dedução da quantia total devida à parte <strong>OUTORGANTE</strong>, com expedição de guias de levantamento individualizadas para cada parte.</p>"
      },
      {
        type: "sub_clause",
        level: 0,
        content: "<p><strong>Compliance e Prevenção à Lavagem de Dinheiro (PLD):</strong> Conferem-se poderes explícitos à parte <strong>OUTORGADA</strong> para, em nome da parte <strong>OUTORGANTE</strong>, preencher, assinar e validar formulários de Compliance Bancário, declarações de origem lícita de recursos e documentações exigidas pelo COAF/Bacen necessárias para a viabilização de levantamentos financeiros e repasses.</p>",
        condition: "ALWAYS",
        id: "cl_06_03_pld"
      },
      {
        id: "sub_1773712457827",
        content: "<p><strong>Gestão de Ativos Virtuais e Criptoeconomia:</strong> Conferem-se poderes explícitos para representar a parte <strong>OUTORGANTE</strong> perante Sociedades Prestadoras de Serviços de Ativos Virtuais (PSAVs) e corretoras de criptoativos (<em>exchanges</em>), autorizando a solicitar a liquidação de <em>tokens</em>, requerer o acesso e transferência de fundos alocados em carteiras digitais (<em>hot wallets</em> e <em>cold wallets</em>), e exercer a gestão de chaves privadas sob custódia fiduciária, conforme os ditames da Resolução BCB n. 520/2025 e do planejamento sucessório, resguardando o patrimônio em casos de inventário ou execução.</p>",
        type: "sub_clause",
        condition: "CRIPTO_ONLY"
      }
    ],
    title: "DOS PODERES FINANCEIROS, PLD E ALVARÁS ELETRÔNICOS"
  },
  {
    type: "sub_clause_group",
    content: "",
    title: "DOS PODERES DIGITAIS E REPRESENTAÇÃO EXTRAJUDICIAL",
    id: "cl_07_digitais",
    children: [
      {
        type: "sub_clause",
        level: 0,
        condition: "ALWAYS",
        content: "<p><strong>Domicílio Judicial Eletrônico:</strong> Fica a parte <strong>OUTORGADA</strong> expressamente autorizada a cadastrar, acessar, gerir e manifestar ciência em citações e intimações no Domicílio Judicial Eletrônico (Res. CNJ n. 569/2024) em nome da parte <strong>OUTORGANTE</strong>. A parte <strong>OUTORGANTE</strong> compromete-se a não acessar a caixa de notificações do sistema sem prévia comunicação à parte <strong>OUTORGADA</strong> e, caso o faça acidentalmente, obriga-se a notificá-la em até 24 (vinte e quatro) horas, permanecendo a cargo da parte <strong>OUTORGADA</strong> a responsabilidade primária pelo acompanhamento regular dos prazos processuais, salvo comprovada omissão de comunicação por parte da <strong>OUTORGANTE</strong>.</p>",
        id: "cl_07_01_intro"
      },
      {
        level: 0,
        content: "<p><strong>Representação Extrajudicial e Administrativa:</strong> Fica a parte <strong>OUTORGADA</strong> autorizada a representar a parte <strong>OUTORGANTE</strong> perante órgãos da Administração Direta e Indireta, autarquias, Cartórios de Registro de Imóveis, Títulos e Documentos e Tabelionatos de Protestos, câmaras privadas de mediação e Juntas Comerciais, podendo assinar termos de acordo, confessar dívidas, requerer certidões, cancelar protestos, promover averbações e assinar escrituras públicas.</p>",
        type: "sub_clause",
        id: "cl_07_01_01_dje",
        condition: "ALWAYS"
      }
    ],
    tags: [
      "Universal"
    ],
    condition: "ALWAYS"
  },
  {
    condition: "ALWAYS",
    tags: [
      "Universal"
    ],
    children: [
      {
        type: "sub_clause",
        content: "<p><strong>Instalação de Inquérito Privado:</strong> Em estrita observância ao Provimento n. 188/2018 do Conselho Federal da OAB, conferem-se à parte <strong>OUTORGADA</strong> poderes explícitos e excepcionais para instaurar, planejar e conduzir procedimentos autônomos de Investigação Criminal Defensiva em proveito da parte <strong>OUTORGANTE</strong>.</p>",
        condition: "INVESTIGACAO_DEFENSIVA_ONLY",
        id: "sub_1773712666527"
      },
      {
        content: "<p>A referida delegação outorga autoridade fiduciária para colher depoimentos, solicitar judicial ou extrajudicialmente dados em provedores de aplicação, incluindo logs de acesso, cópias de segurança (backups) e dados cadastrais atrelados às contas de titularidade da parte <strong>OUTORGANTE</strong>, oficiar repartições e contratar peritos forenses especializados, cujos honorários e despesas operacionais correrão às expensas exclusivas da parte <strong>OUTORGANTE</strong>, visando o adensamento robusto do acervo probatório em defesa dos interesses constituídos.</p>",
        condition: "INVESTIGACAO_DEFENSIVA_ONLY",
        level: 1,
        id: "sub_1773712679548",
        type: "sub_clause"
      },
      {
        content: "<p><strong>Autorização de Publicidade e Sigilo Estratégico:</strong> Em estrito atendimento ao art. 6º, parágrafo único, do Provimento n. 188/2018 do Conselho Federal da OAB, a parte <strong>OUTORGANTE</strong> autoriza expressamente a publicidade, a anexação forense e a comunicação parcial ou total dos resultados e laudos da presente investigação defensiva às autoridades competentes.</p>",
        id: "sub_1773712687076",
        condition: "INVESTIGACAO_DEFENSIVA_ONLY",
        type: "sub_clause"
      },
      {
        content: "A parte <strong>OUTORGADA</strong> resguarda-se, sob a égide do sigilo profissional, o privilégio estratégico de não acostar aos autos elementos informativos que repute prejudiciais à defesa técnica.",
        condition: "INVESTIGACAO_DEFENSIVA_ONLY",
        id: "sub_1773712709417",
        level: 1,
        type: "sub_clause"
      }
    ],
    title: "DA INVESTIGAÇÃO DEFENSIVA",
    type: "sub_clause_group",
    content: "",
    items: null,
    id: "proc_new_1773712652423"
  },
  {
    id: "cl_compliance_interno_add",
    condition: "ALWAYS",
    tags: [
      "Universal"
    ],
    title: "DO COMPLIANCE CORPORATIVO E DA INVESTIGAÇÃO INTERNA",
    type: "sub_clause_group",
    content: "",
    items: null,
    children: [
      {
        id: "cl_compliance_01_instauracao",
        type: "sub_clause",
        condition: "COMPLIANCE_INTERNO_ONLY",
        content: "<p><strong>Investigação Interna e Programa de Integridade:</strong> Em conformidade com a Lei n. 12.846/2013 (Lei Anticorrupção) e o Decreto n. 11.129/2022, que regulamentam os programas de integridade e os mecanismos internos de apuração de irregularidades, conferem-se à parte <strong>OUTORGADA</strong> poderes específicos para conduzir ou assessorar procedimentos de investigação interna, auditorias de conformidade (compliance) e apuração de fatos em proveito da parte <strong>OUTORGANTE</strong>, com vistas à formação de acervo probatório para fins de autorregulação, eventual celebração de acordo de leniência ou aprimoramento do programa de integridade.</p>"
      },
      {
        id: "cl_compliance_02_colheita",
        type: "sub_clause",
        level: 1,
        condition: "COMPLIANCE_INTERNO_ONLY",
        content: "<p>A referida delegação autoriza a colheita de depoimentos e entrevistas de colaboradores, a análise de documentos e sistemas corporativos, e a contratação de peritos e consultores técnicos especializados, cujos honorários e despesas operacionais correrão às expensas exclusivas da parte <strong>OUTORGANTE</strong>.</p>"
      },
      {
        id: "cl_compliance_03_sigilo",
        type: "sub_clause",
        condition: "COMPLIANCE_INTERNO_ONLY",
        content: "<p><strong>Confidencialidade e Privilégio Legal:</strong> Os resultados da investigação interna, incluindo relatórios, entrevistas e documentos produzidos, são protegidos pelo sigilo profissional e pelo privilégio legal advogado-cliente, cabendo exclusivamente à parte <strong>OUTORGANTE</strong> decidir sobre eventual compartilhamento com autoridades públicas ou órgãos reguladores.</p>"
      }
    ]
  },
  {
    children: [
      {
        type: "sub_clause",
        id: "sub_1773703365222",
        content: "<p><strong>Confissão Extrajudicial de Identidade Telemática (WhatsApp/Telegram):</strong> Sob a égide da vedação ao comportamento contraditório (<em>venire contra factum proprium</em>), a parte <strong>OUTORGANTE</strong> declara e confessa formalmente ser a única e exclusiva titular do número de telefone celular provido de aplicativo de mensagens (WhatsApp/Telegram) expressamente declinado em sua qualificação na [[REF_CLAUSE:cl_01_partes]] deste instrumento.</p>",
        condition: "ALWAYS"
      },
      {
        type: "sub_clause",
        condition: "ALWAYS",
        id: "sub_1773703369741",
        content: "<p>Fica o Poder Judiciário, as serventias e a parte <strong>OUTORGADA</strong> expressamente autorizados a utilizar este canal para o envio de mandados de citação inicial, intimações e <em>links</em> de audiência. A parte <strong>OUTORGANTE</strong> reconhece a validade incontestável da ciência gerada por este meio, renunciando antecipadamente a qualquer futura alegação de nulidade processual por vício de identificação, e assumindo o dever de manter o referido número atualizado e de informar imediatamente qualquer alteração, sob pena de serem reputadas válidas e eficazes as comunicações enviadas ao número aqui declinado.</p>"
      },
      {
        id: "sub_1785274529847",
        condition: "ALWAYS",
        content: "<p><strong>Exceção às Ações de Estado:</strong> Ressalvam-se desta cláusula as ações de estado (aquelas que versam sobre estado civil, capacidade da pessoa, divórcio, separação, união estável, filiação, tutela, curatela e guarda), nas quais a citação deverá observar o rito pessoal previsto no art. 247, I, da Lei n. 13.105/2015 (Código de Processo Civil), não se aplicando a autorização de citação telemática conferida no item anterior desta cláusula, sem prejuízo da validade da confissão de titularidade do número declarada no item inicial desta cláusula, para os demais atos de comunicação processual.</p>",
        type: "sub_clause"
      }
    ],
    items: null,
    condition: "ALWAYS",
    tags: [
      "Universal"
    ],
    title: "DA CONFISSÃO EXTRAJUDICIAL DE IDENTIDADE E CITAÇÃO TELEMÁTICA",
    id: "proc_new_1773703255153",
    type: "sub_clause_group",
    listStyle: "NUM",
    content: ""
  },
  {
    id: "cl_08_lgpd",
    children: [
      {
        level: 0,
        content: "<p><strong>Legitimação em Instâncias Não Estatais:</strong> Frente à busca incessante por soluções consensuais, a parte <strong>OUTORGANTE</strong> confere poderes especiais à parte <strong>OUTORGADA</strong> para representá-la perante toda e qualquer Câmara Privada de Mediação, Conciliação e Arbitragem (físicas ou em plataformas de <em>Online Dispute Resolution</em> — ODR).</p>",
        id: "cl_08_01_lgpd_fluxo",
        type: "sub_clause",
        condition: "ARBITRAGEM_ONLY"
      },
      {
        id: "cl_08_02_ia",
        content: "<p>A parte <strong>OUTORGADA</strong> dispõe de capacidade técnica para deflagrar procedimentos arbitrais, firmar Termo de Arbitragem (Ata de Missão ou Termo de Referência), estipular convenções processuais, nomear ou impugnar árbitros e transigir de forma irrestrita sobre direitos patrimoniais disponíveis. A parte <strong>OUTORGANTE</strong> reconhece que a Sentença Arbitral prolatada, inclusive a decorrente de homologação de acordo privado, assumirá inequivocamente a força de título executivo judicial irrecorrível no mérito, nos moldes da Lei n. 9.307/1996 (Lei de Arbitragem).</p>",
        type: "sub_clause",
        level: 0,
        condition: "ARBITRAGEM_ONLY"
      }
    ],
    condition: "ALWAYS",
    tags: [
      "Universal"
    ],
    type: "sub_clause_group",
    content: "",
    title: "DA REPRESENTAÇÃO MULTI-PORTAS E ARBITRAGEM (ODR)"
  },
  {
    condition: "ALWAYS",
    type: "sub_clause_group",
    title: "DA FACULDADE DE SUBSTABELECIMENTO",
    content: "",
    id: "proc_new_1773712834828",
    items: null,
    listStyle: "NUM",
    children: [
      {
        content: "<p>Fica expressamente autorizada a faculdade de substabelecer os poderes aqui conferidos à parte <strong>OUTORGADA</strong>, no todo ou em parte, com ou sem reserva de iguais poderes, admitindo-se o substabelecimento eletrônico para a plena e ininterrupta condução dos interesses da parte <strong>OUTORGANTE</strong>.</p>",
        condition: "ALWAYS",
        id: "sub_1773712864298",
        type: "sub_clause"
      }
    ],
    tags: [
      "Universal"
    ]
  },
  {
    id: "cl_09_lgpd_add",
    title: "DA CONFORMIDADE COM A LEI GERAL DE PROTEÇÃO DE DADOS (LGPD)",
    type: "sub_clause_group",
    tags: [
      "Universal"
    ],
    condition: "ALWAYS",
    children: [
      {
        id: "cl_09_lgpd_01_base",
        type: "sub_clause",
        condition: "ALWAYS",
        level: 0,
        content: "<p><strong>Da Base Legal do Tratamento</strong>: o tratamento dos dados pessoais da parte <strong>OUTORGANTE</strong> (incluindo, quando aplicável, dados sensíveis necessários à instrução da causa) pela parte <strong>OUTORGADA</strong>, no exercício dos poderes conferidos por este instrumento, independe de consentimento, fundamentando-se na hipótese legal de <strong>Exercício Regular de Direitos em Processo Judicial, Administrativo ou Arbitral</strong> (art. 7º, VI, da Lei n. 13.709/2018 — LGPD).</p>"
      },
      {
        id: "cl_09_lgpd_02_escopo",
        type: "sub_clause",
        condition: "ALWAYS",
        level: 0,
        content: "<p>O tratamento abrange a coleta, o armazenamento, a utilização e o compartilhamento dos dados estritamente necessários ao exercício dos poderes outorgados, incluindo a comunicação com o Poder Judiciário, órgãos públicos, instituições financeiras e demais terceiros perante os quais a parte <strong>OUTORGADA</strong> venha a atuar em nome da parte <strong>OUTORGANTE</strong>.</p>"
      },
      {
        id: "cl_09_lgpd_03_medidas",
        type: "sub_clause",
        condition: "ALWAYS",
        level: 0,
        content: "<p>A parte <strong>OUTORGADA</strong> compromete-se a adotar as medidas técnicas e administrativas cabíveis para proteger os dados pessoais da parte <strong>OUTORGANTE</strong> contra acessos não autorizados e situações de destruição, perda ou alteração, em conformidade com a Lei n. 13.709/2018 (LGPD).</p>"
      },
      {
        id: "cl_09_lgpd_04_direitos",
        type: "sub_clause",
        condition: "ALWAYS",
        level: 0,
        content: "<p>A parte <strong>OUTORGANTE</strong> declara-se ciente de seus direitos como titular dos dados, incluindo acesso, retificação, eliminação e portabilidade, exercitáveis mediante solicitação formal à parte <strong>OUTORGADA</strong>, sem prejuízo da manutenção dos dados estritamente necessários ao cumprimento de obrigação legal, nos termos do art. 16, I, da mesma Lei, ou ao exercício regular de direitos em processo, nos termos do art. 7º, VI, da mesma Lei.</p>"
      }
    ],
    content: ""
  },
  {
    id: "cl_08_eficacia",
    condition: "ALWAYS",
    content: "",
    children: [
      {
        level: 0,
        id: "cl_08_01_indeterminado",
        type: "sub_clause",
        condition: "VALIDADE_INDETERMINADO",
        content: "Em observância à jurisprudência pacificada do Superior Tribunal de Justiça (STJ), esta procuração possui validade por prazo indeterminado, produzindo efeitos até formal revogação. O simples decurso do tempo, por si só, não constitui causa lícita para a recusa deste instrumento, sem prejuízo da faculdade do juízo de, motivadamente e com base em indícios concretos de litigância abusiva (Tema 1.198/STJ), solicitar a apresentação de instrumento atualizado."
      },
      {
        type: "sub_clause",
        level: 0,
        content: "A presente procuração possui validade estrita até o dia [[VALIDADE_DATA]].",
        condition: "VALIDADE_DETERMINADO",
        id: "cl_08_02_determinado"
      },
      {
        condition: "VALIDADE_ATO_ESPECIFICO",
        type: "sub_clause",
        id: "cl_08_03_ato_especifico",
        content: "A presente procuração é outorgada exclusivamente para o seguinte fim específico: [[VALIDADE_ATO]].",
        level: 0
      },
      {
        content: "<p>As partes e os juízos destinatários devem reconhecer a presunção de veracidade da assinatura eletrônica aposta neste instrumento. Tratando-se de assinatura via plataforma avançada (com rastreio de IP e <em>Liveness Detection</em> - MP n. 2.200-2/2001) ou via portal federal Gov.br (Lei n. 14.063/2020 — Lei das Assinaturas Eletrônicas), o ato equipara-se à assinatura física com firma reconhecida para todos os fins, sendo vedada a exigência de ratificação notarial presencial por configurar excesso de formalismo, salvo mediante impugnação específica e fundamentada de falsidade, nos termos do julgamento do REsp n. 2.243.445/SP e determinações do CNJ.</p>",
        type: "sub_clause",
        id: "cl_08_04_assinatura_presuncao",
        condition: "ALWAYS",
        level: 0
      }
    ],
    type: "sub_clause_group",
    title: "DA EFICÁCIA ATEMPORAL E INTEGRIDADE DA ASSINATURA",
    tags: [
      "Universal"
    ]
  }
];

export const PROCURATION_CLAUSES = POWERS_LIBRARY;

// --- TEMPLATES DE MONTAGEM (RECEITAS) ---
const MASTER_MINUTE_STRUCTURE = [
  "preambulo",
  "cl_01_partes",
  "cl_02_objeto",
  "cl_04_especiais_comuns",
  "cl_05_setorial",
  "cl_06_financeiros",
  "cl_07_digitais",
  "proc_new_1773712652423",
  "cl_compliance_interno_add",
  "proc_new_1773703255153",
  "cl_08_lgpd",
  "proc_new_1773712834828",
  "cl_09_lgpd_add",
  "cl_08_eficacia"
];

export const SYSTEM_TEMPLATES = [
  {
    id: "tpl_master_v95",
    title: "Procuração — Minuta Mestre (V95)",
    structure: MASTER_MINUTE_STRUCTURE,
    tags: [
      "Civil",
      "Penal",
      "Trabalho",
      "Previdenciario",
      "Administrativo",
      "Tributario",
      "Familia",
      "Animal",
    ],
  },
];
